import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RotateLeft as RotateLeftIcon,
    RotateRight as RotateRightIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';

const DocumentPreviewHandler = ({ file }) => {
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!file) {
            console.log('No file provided');
            setLoading(false);
            return;
        }

        console.log('Loading file preview:', file.name, file.type);

        // Handle preview based on file type
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadstart = () => setLoading(true);
            reader.onloadend = () => {
                console.log('Image loaded successfully');
                setPreview(reader.result);
                setLoading(false);
            };
            reader.onerror = (error) => {
                console.error('Error reading file:', error);
                setError('Nie można wczytać pliku. Spróbuj ponownie.');
                setLoading(false);
            };
            reader.readAsDataURL(file);
        } else if (file.type === 'application/pdf') {
            try {
                const fileUrl = URL.createObjectURL(file);
                console.log('PDF URL created:', fileUrl);
                setPreview(fileUrl);
                setLoading(false);
            } catch (err) {
                console.error('Error creating object URL:', err);
                setError('Nie można wygenerować podglądu PDF.');
                setLoading(false);
            }

            // Cleanup function for PDF preview
            return () => {
                if (preview) {
                    URL.revokeObjectURL(preview);
                }
            };
        } else {
            setError(`Nieobsługiwany typ pliku: ${file.type}`);
            setLoading(false);
        }
    }, [file]);

    if (!file) {
        return (
            <Alert severity="info">
                Najpierw wybierz plik, aby zobaczyć podgląd.
            </Alert>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Podgląd dokumentu
                </Typography>
                <Box>
                    <IconButton onClick={() => setZoom(prev => Math.min(prev + 0.1, 2))}>
                        <ZoomInIcon />
                    </IconButton>
                    <IconButton onClick={() => setZoom(prev => Math.max(prev - 0.1, 0.5))}>
                        <ZoomOutIcon />
                    </IconButton>
                    <IconButton onClick={() => setRotation(prev => (prev - 90) % 360)}>
                        <RotateLeftIcon />
                    </IconButton>
                    <IconButton onClick={() => setRotation(prev => (prev + 90) % 360)}>
                        <RotateRightIcon />
                    </IconButton>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box
                sx={{
                    height: '500px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'hidden'
                }}
            >
                {loading ? (
                    <CircularProgress />
                ) : !preview ? (
                    <Typography color="textSecondary">
                        Nie można wyświetlić podglądu dla tego typu pliku.
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'auto'
                        }}
                    >
                        {file.type === 'application/pdf' ? (
                            <iframe
                                src={preview}
                                title="PDF Preview"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    backgroundColor: 'white'
                                }}
                            />
                        ) : (
                            <img
                                src={preview}
                                alt="Document preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    transform: `rotate(${rotation}deg) scale(${zoom})`,
                                    transition: 'transform 0.3s ease'
                                }}
                                onError={(e) => {
                                    console.error('Error loading image:', e);
                                    setError('Nie można załadować obrazu.');
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default DocumentPreviewHandler;